export type Language = 'en' | 'fr'

export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      pricing: 'Pricing',
      clinic: 'FGM Hope Clinic',
      contact: 'Contact',
      bookNow: 'Book Now',
    },
    hero: {
      headline: 'Compassionate Midwifery Care in Auckland and Online',
      subheadline:
        'Personalized support for pregnancy, birth preparation, and lactation — with gentle, expert care in English and French.',
      ctaBook: 'Book a consultation',
      ctaServices: 'View services',
      badges: {
        location: 'Based in Auckland',
        virtual: 'Virtual consultations available',
        languages: 'English & French',
        trauma: 'Trauma-informed care',
      },
    },
    about: {
      title: 'About Aissa',
      bio1: 'Aissa Edon is a compassionate midwife based in Auckland, supporting women and families with personalized, culturally sensitive care.',
      bio2: "She also offers virtual consultations, making her services accessible beyond New Zealand. Her work combines professional expertise, warmth, and deep respect for each woman's unique journey.",
      whyWorkTitle: 'Why work with Aissa?',
      points: [
        'Gentle and personalized support',
        'English and French consultations',
        'Auckland-based with online sessions available',
        'Professional, human, culturally sensitive care',
      ],
    },
    services: {
      title: 'Services',
      subtitle: 'Gentle, expert care tailored to your unique journey.',
      lactation: {
        title: 'Lactation Consultations',
        desc: 'One-on-one support to help mothers feel confident, informed, and supported with breastfeeding and feeding challenges.',
        cta: 'Learn more',
      },
      birthPrep: {
        title: 'Empowered Birth Preparation',
        desc: 'Personalized birth preparation sessions designed to help women feel informed, calm, and empowered before labour and birth.',
        cta: 'Learn more',
      },
      breastfeeding: {
        title: 'Breastfeeding Support',
        desc: 'Ongoing breastfeeding support for mothers who need guidance, reassurance, and practical help.',
        status: 'Coming soon – September',
        cta: 'Learn more',
      },
    },
    pricing: {
      title: 'Pricing',
      subtitle: 'Transparent pricing for personalized care.',
      note: 'Prices are listed in New Zealand Dollars, with approximate US Dollar equivalents for international clients.',
      cards: {
        lactation: {
          title: 'Lactation Consultation',
          nzd: 'NZD $150',
          usd: 'Approx. USD $90',
        },
        birthSingle: {
          title: 'Empowered Birth Preparation',
          subtitle: 'Single Session',
          nzd: 'NZD $150',
          usd: 'Approx. USD $90',
        },
        birthPackage: {
          title: 'Empowered Birth Preparation',
          subtitle: '7 Session Package',
          nzd: 'NZD $900',
          usd: 'Approx. USD $540',
        },
        breastfeeding: {
          title: 'Breastfeeding Support',
          status: 'Coming Soon – September',
        },
      },
    },
    fgm: {
      title: 'FGM Hope Clinic',
      text: "FGM Hope Clinic is an important part of Aissa Edon's mission to support, educate, and advocate for women affected by FGM. We provide a safe, respectful, and compassionate environment for care and healing.",
      button: 'Learn more',
    },
    booking: {
      title: 'Ready to feel supported?',
      text: 'Book a consultation with Aissa and receive gentle, professional care tailored to your needs — in Auckland or online.',
      btnBook: 'Book now',
      btnContact: 'Contact Aissa',
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Reach out to schedule a consultation or ask any questions.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        message: 'How can I help you?',
        submit: 'Send Message',
      },
      info: {
        location: 'Auckland, New Zealand',
        virtual: 'Virtual consultations available worldwide',
        email: 'hello@aissaedon.com',
      },
    },
    footer: {
      name: 'Aissa Edon',
      role: 'Midwifery Care | Auckland & Online',
      rights: 'All rights reserved.',
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      about: 'À propos',
      services: 'Services',
      pricing: 'Tarifs',
      clinic: 'FGM Hope Clinic',
      contact: 'Contact',
      bookNow: 'Réserver',
    },
    hero: {
      headline:
        'Accompagnement bienveillant par une sage-femme à Auckland et en ligne',
      subheadline:
        "Un accompagnement personnalisé pour la grossesse, la préparation à la naissance et l'allaitement — avec une approche douce, experte et disponible en français et en anglais.",
      ctaBook: 'Prendre rendez-vous',
      ctaServices: 'Voir les services',
      badges: {
        location: 'Basée à Auckland',
        virtual: 'Consultations virtuelles disponibles',
        languages: 'Anglais & Français',
        trauma: 'Soins tenant compte des traumatismes',
      },
    },
    about: {
      title: "À propos d'Aissa",
      bio1: 'Aissa Edon est une sage-femme bienveillante basée à Auckland, accompagnant les femmes et les familles avec des soins personnalisés et respectueux de leur culture.',
      bio2: 'Elle propose également des consultations virtuelles, rendant ses services accessibles au-delà de la Nouvelle-Zélande. Son travail allie expertise professionnelle, chaleur humaine et un profond respect pour le parcours unique de chaque femme.',
      whyWorkTitle: 'Pourquoi choisir Aissa ?',
      points: [
        'Accompagnement doux et personnalisé',
        'Consultations en anglais et en français',
        'Basée à Auckland avec sessions en ligne disponibles',
        'Soins professionnels, humains et respectueux',
      ],
    },
    services: {
      title: 'Services',
      subtitle: 'Des soins doux et experts adaptés à votre parcours unique.',
      lactation: {
        title: 'Consultations en lactation',
        desc: "Un accompagnement individuel pour aider les mères à se sentir confiantes, informées et soutenues face aux défis de l'allaitement.",
        cta: 'En savoir plus',
      },
      birthPrep: {
        title: 'Préparation à la naissance',
        desc: "Des séances de préparation à la naissance personnalisées conçues pour aider les femmes à se sentir informées, calmes et autonomes avant le travail et l'accouchement.",
        cta: 'En savoir plus',
      },
      breastfeeding: {
        title: "Soutien à l'allaitement",
        desc: "Un soutien continu à l'allaitement pour les mères qui ont besoin de conseils, de réassurance et d'aide pratique.",
        status: 'Bientôt disponible – Septembre',
        cta: 'En savoir plus',
      },
    },
    pricing: {
      title: 'Tarifs',
      subtitle: 'Des tarifs transparents pour des soins personnalisés.',
      note: 'Les prix sont indiqués en dollars néo-zélandais, avec des équivalents approximatifs en dollars américains pour les clients internationaux.',
      cards: {
        lactation: {
          title: 'Consultation en lactation',
          nzd: 'NZD $150',
          usd: 'Env. USD $90',
        },
        birthSingle: {
          title: 'Préparation à la naissance',
          subtitle: 'Séance unique',
          nzd: 'NZD $150',
          usd: 'Env. USD $90',
        },
        birthPackage: {
          title: 'Préparation à la naissance',
          subtitle: 'Forfait 7 séances',
          nzd: 'NZD $900',
          usd: 'Env. USD $540',
        },
        breastfeeding: {
          title: "Soutien à l'allaitement",
          status: 'Bientôt disponible – Septembre',
        },
      },
    },
    fgm: {
      title: 'FGM Hope Clinic',
      text: "La clinique FGM Hope est une part importante de la mission d'Aissa Edon pour soutenir, éduquer et défendre les femmes touchées par les MGF. Nous offrons un environnement sûr, respectueux et bienveillant pour les soins et la guérison.",
      button: 'En savoir plus',
    },
    booking: {
      title: 'Prête à vous sentir soutenue ?',
      text: 'Prenez rendez-vous avec Aissa et recevez des soins doux et professionnels adaptés à vos besoins — à Auckland ou en ligne.',
      btnBook: 'Réserver maintenant',
      btnContact: 'Contacter Aissa',
    },
    contact: {
      title: 'Contact',
      subtitle:
        "N'hésitez pas à nous contacter pour planifier une consultation ou poser vos questions.",
      form: {
        name: 'Votre Nom',
        email: 'Adresse Email',
        message: 'Comment puis-je vous aider ?',
        submit: 'Envoyer le message',
      },
      info: {
        location: 'Auckland, Nouvelle-Zélande',
        virtual: 'Consultations virtuelles disponibles dans le monde entier',
        email: 'hello@aissaedon.com',
      },
    },
    footer: {
      name: 'Aissa Edon',
      role: 'Soins de sage-femme | Auckland & en ligne',
      rights: 'Tous droits réservés.',
    },
  },
}
